import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

export type Lang = 'tr' | 'en';

const dict: Record<Lang, Record<string, string>> = {
  tr: {
    home: 'Anasayfa',
    compare: 'Karşılaştır',
    watchlist: 'Takip listem',
    rising: 'Yükselenler',
    newForums: 'Yeni eklenenler',
    categories: 'Kategoriler',
    addForum: 'Yeni forum ekle',
    embed: 'Embed',
    filters: 'Filtreler',
    minMembers: 'Min. üye',
    last7days: 'Son 7 günde güncellenen',
    hideSponsored: 'Sponsorluları gizle',
    growthSort: 'Büyüme',
    software: 'Forum yazılımları',
    inactive: 'Forum mezarlığı',
    explore: 'Keşfet',
    guide: 'Rehber',
    navSoftware: 'Yazılımlar',
    navAutomobile: 'Otomobil',
    navInactive: 'Mezarlık',
    navAddForum: 'Forum ekle',
    navWatchlist: 'Takip listem',
    navNewForums: 'Yeni eklenenler',
    navCommunity: 'Yeni & öner',
    communityPageTitle: 'Forum topluluğu',
    communityPageIntro:
      'Son eklenen forumları görün, bekleyen önerileri destekleyin veya yeni bir forum önerin.',
    communityTabNew: 'Yeni eklenenler',
    communityTabPending: 'Bekleyen öneriler',
    communityTabSuggest: 'Forum öner',
    communityBack: 'Geri',
    communityNoNewForums: 'Henüz listelenen yeni forum yok.',
    leaderboard: 'Lig tablosu',
    tfeIndex: 'Forum endeksi',
    weeklySummary: 'Haftalık özet',
    monthlySummary: 'Aylık özet',
    hallOfFame: 'Hall of Fame',
    advancedSearch: 'Gelişmiş arama',
    compareCategories: 'Kategori kıyas',
    apiDocs: 'API',
    navSponsor: 'Sponsor ol',
  },
  en: {
    home: 'Home',
    compare: 'Compare',
    watchlist: 'My watchlist',
    rising: 'Rising',
    newForums: 'Newly added',
    categories: 'Categories',
    addForum: 'Suggest forum',
    embed: 'Embed',
    filters: 'Filters',
    minMembers: 'Min. members',
    last7days: 'Updated in 7 days',
    hideSponsored: 'Hide sponsored',
    growthSort: 'Growth',
    software: 'Forum software',
    inactive: 'Forum graveyard',
    explore: 'Explore',
    guide: 'Guide',
    navSoftware: 'Software',
    navAutomobile: 'Automobiles',
    navInactive: 'Graveyard',
    navAddForum: 'Add forum',
    navWatchlist: 'Watchlist',
    navNewForums: 'New forums',
    navCommunity: 'New & suggest',
    communityPageTitle: 'Forum community',
    communityPageIntro: 'See newly added forums, support pending suggestions, or suggest a new forum.',
    communityTabNew: 'Newly added',
    communityTabPending: 'Pending suggestions',
    communityTabSuggest: 'Suggest forum',
    communityBack: 'Back',
    communityNoNewForums: 'No newly listed forums yet.',
    leaderboard: 'Leaderboard',
    tfeIndex: 'Forum index',
    weeklySummary: 'Weekly summary',
    monthlySummary: 'Monthly summary',
    hallOfFame: 'Hall of Fame',
    advancedSearch: 'Advanced search',
    compareCategories: 'Compare categories',
    apiDocs: 'API',
    navSponsor: 'Become a sponsor',
  },
};

type Ctx = { lang: Lang; setLang: (l: Lang) => void; t: (key: string) => string };

const LanguageContext = createContext<Ctx | null>(null);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    const s = localStorage.getItem('site_lang');
    return s === 'en' ? 'en' : 'tr';
  });

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    localStorage.setItem('site_lang', l);
    document.documentElement.lang = l;
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback(
    (key: string) => dict[lang][key] ?? dict.tr[key] ?? key,
    [lang]
  );

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage requires LanguageProvider');
  return ctx;
}
